import React, { useState } from 'react';
import './WallDrawer.css';
import IdeaIcon from '../../assets/IdeaIcon.png';
import QuestionIcon from '../../assets/QuestionIcon.png';
import InformationIcon from '../../assets/InformationIcon.png';
import FlaskIcon from '../../assets/FlaskIcon.png';
import NoteIcon from '../../assets/NoteIcon.png';
import { CreateIdea } from '../CreateIdea';
import { CreateQuestion } from '../CreateQuestion';
import { CreateInformation } from '../CreateInformation';
import { CreateFlask } from '../CreateFlask';
import { CreateNote } from '../CreateNote';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemButton, ListItemText, Typography, Box } from '@mui/material';

const WallDrawer = ({ 
  currentWall, 
  onWallChange, 
  onNodeTypeSelect,
  wallConfig = {},
  nodeTypes = [],
  visualizationMode = 'vis-network',
  onVisualizationModeChange,
  // 篩選功能相關 props
  timeFilter = 0,
  groupFilter = 'all',
  keywordFilter = '',
  onTimeFilterChange,
  onGroupFilterChange,
  onKeywordFilterChange,
  onFilterButtonClick,
  // WebSocket 連接
  ws,
  // 節點創建回調
  onNodeCreated,
  // 禁用創建節點（用於跨組查看時）
  disableCreateNode = false,
  // 小組選擇相關 props
  allGroups = [],
  myGroupId = null,
  viewingGroupId = null,
  onGroupChange = () => {},
  isGroupSelectDialogOpen = false,
  onGroupSelectDialogOpen = () => {},
  // 是否允許跨組查看（由老師控制）
  allowCrossGroupView = true
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [selectedModal, setSelectedModal] = useState(null);
  const [selectedModalOpen, setSelectedModalOpen] = useState(false);

  const walls = [
    { key: 'idea', label: '想法牆', icon: '💡', color: 'idea' },
    { key: 'question', label: '問題牆', icon: '❓', color: 'question' },
    { key: 'cross_group', label: '跨組討論', icon: '💬', color: 'cross_group' }
  ];

  const defaultNodeTypes = [
    { key: 'idea', label: '想法', icon: IdeaIcon },
    { key: 'question', label: '問題', icon: QuestionIcon },
    { key: 'information', label: '資訊', icon: InformationIcon },
    { key: 'experiment', label: '實驗', icon: FlaskIcon },
    { key: 'record', label: '記錄', icon: NoteIcon }
  ];

  // 模仿 ForumPage_Navbar.js 的 defaultNavBarItems
  // 节点类型颜色映射（与 NodeCreator.js 一致）
  const nodeTypeColors = {
    'idea': '#FFF9C4',         // 想法 - 黃色
    'question': '#BBDEFB',     // 提問 - 藍色
    'info': '#C8E6C9',         // 資訊 - 綠色
    'experiment': '#FFCDD2',   // 實驗 - 紅色
    'record': '#B2EBF2'        // 記錄 - 青色
  };

  const defaultNavBarItems = [
    { text: "新增想法", modalKey: "createIdea", icon: "IdeaIcon", color: nodeTypeColors['idea'] },
    { text: "新增提問", modalKey: "createQuestion", icon: "QuestionIcon", color: nodeTypeColors['question'] },
    { text: "新增資訊", modalKey: "createInformation", icon: "InformationIcon", color: nodeTypeColors['info'] },
    { text: "新增實驗", modalKey: "createFlask", icon: "FlaskIcon", color: nodeTypeColors['experiment'] },
    { text: "新增紀錄", modalKey: "createNote", icon: "NoteIcon", color: nodeTypeColors['record'] },
  ];

  const nodeTypesToShow = nodeTypes.length > 0 ? nodeTypes : defaultNodeTypes;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleWallChange = (wallKey) => {
    if (wallConfig[wallKey] !== false) {
      onWallChange(wallKey);
    }
  };

  const handleNodeTypeSelect = (nodeType) => {
    onNodeTypeSelect(nodeType);
  };

  // 圖標映射函數
  const getIconByLabel = (label) => {
    switch (label) {
      case "IdeaIcon":
        return IdeaIcon;
      case "QuestionIcon":
        return QuestionIcon;
      case "InformationIcon": 
        return InformationIcon;
      case "FlaskIcon":
        return FlaskIcon;
      case "NoteIcon": 
        return NoteIcon;
      default:
        return NoteIcon;
    }
  };

  // 打開模態框
  const openModal = (modalKey) => {
    setSelectedModal(modalKey);
    setSelectedModalOpen(true);
  };

  // 關閉模態框
  const closeModal = () => {
    setSelectedModal(null);
    setSelectedModalOpen(false);
    // 清理狀態，確保下次打開時是乾淨的
    setTimeout(() => {
      setSelectedModal(null);
      setSelectedModalOpen(false);
    }, 100);
  };

  // 渲染選中的模態框
  const renderSelectedModal = (label) => {
    // 獲取當前牆面類型，用於決定節點應該創建在哪個牆面
    const currentWallType = currentWall === 'idea' ? 'idea' : 
                            currentWall === 'question' ? 'question' : 
                            currentWall === 'cross_group' ? 'cross_group' : 'idea';
    
    // 獲取 courseDispatchId 和 groupId（從 sessionStorage）
    const courseDispatchId = sessionStorage.getItem('courseDispatchId') || sessionStorage.getItem('activityId');
    const groupId = sessionStorage.getItem('groupId');
    
    switch (label) {
      case "createIdea":
        // 在想法牆中使用"新增想法"時，創建 node_type="idea", wall_type="idea"
        return <CreateIdea 
          open={selectedModalOpen} 
          onClose={closeModal} 
          ws={ws} 
          onNodeCreated={onNodeCreated}
          wallType={currentWallType}
        />;
      case "createQuestion":
        // 在想法牆中使用"新增提問"時，創建 node_type="question", wall_type="idea"
        // 注意：雖然 node_type 是 "question"，但 wall_type 是 "idea"，因為是在想法牆中創建的
        return <CreateQuestion 
          open={selectedModalOpen} 
          onClose={closeModal} 
          ws={ws} 
          onNodeCreated={onNodeCreated}
          wallType={currentWallType}
          courseDispatchId={courseDispatchId}
          groupId={groupId}
        />;
      case "createInformation":
        return <CreateInformation open={selectedModalOpen} onClose={closeModal} ws={ws} onNodeCreated={onNodeCreated} />;
      case "createFlask":
        return <CreateFlask open={selectedModalOpen} onClose={closeModal} ws={ws} onNodeCreated={onNodeCreated} />;
      case "createNote":
        return <CreateNote open={selectedModalOpen} onClose={closeModal} ws={ws} onNodeCreated={onNodeCreated} />;
      default:
        return null;
    }
  };

  return (
    <div className={`wall-drawer ${isExpanded ? 'expanded' : ''}`}>
      {/* 展開/收合按鈕 */}
      <button 
        className="drawer-toggle"
        onClick={toggleExpanded}
        title={isExpanded ? '收合抽屜' : '展開抽屜'}
      >
        {isExpanded ? '‹' : '›'}
      </button>

      {/* Node類型選擇區域 - 僅在想法牆顯示 */}
      {currentWall === 'idea' && (
        <div className="node-type-section">
          <div className="section-title">新增節點</div>
          {disableCreateNode ? (
            <div style={{
              padding: '10px 8px',
              backgroundColor: '#fff3cd',
              borderRadius: '8px',
              border: '1px solid #ffc107',
              fontSize: '11px',
              color: '#856404',
              textAlign: 'center',
              marginTop: '8px',
              lineHeight: '1.4'
            }}>
              <div style={{ marginBottom: '6px', fontSize: '14px' }}>ℹ️</div>
              <div style={{ marginBottom: '4px' }}>您正在查看其他小組的想法牆</div>
              <div style={{ fontSize: '10px', marginTop: '4px', lineHeight: '1.3' }}>
                要創建新節點，請切換到自己的小組
              </div>
            </div>
          ) : (
            defaultNavBarItems.map((menuItem) => (
              <button
                key={menuItem.modalKey}
                className="node-type-button wall-tooltip"
                onClick={() => openModal(menuItem.modalKey)}
                title={menuItem.text}
                data-tooltip={menuItem.text}
                style={{
                  '--hover-color': menuItem.color || '#f0f0f0'
                }}
              >
                <img 
                  src={getIconByLabel(menuItem.icon)} 
                  alt={menuItem.text} 
                  style={{ width: 20, height: 20 }} 
                />
                <span className="button-label">{menuItem.text}</span>
              </button>
            ))
          )}
        </div>
      )}

      {/* 工具區塊（視覺化模式、篩選條件、小組選擇） */}
      {currentWall === 'idea' && (
        <div className="tools-section">
          <div className="section-title">工具</div>
          <div className="tools-buttons">
            {/* 視覺化模式切換 */}
            {onVisualizationModeChange && (
              <button 
                className="mode-button active"
                onClick={() => onVisualizationModeChange(visualizationMode === 'vis-network' ? 'd3' : 'vis-network')}
                title={visualizationMode === 'vis-network' ? '模式一' : '模式二'}
              >
                <span className="mode-icon">{visualizationMode === 'vis-network' ? '📊' : '🌟'}</span>
                <span className="button-label">{visualizationMode === 'vis-network' ? '模式一' : '模式二'}</span>
              </button>
            )}
            
            {/* 篩選工具 */}
            <button 
              className="filter-toggle-button"
              onClick={onFilterButtonClick}
              title="篩選條件"
            >
              <span className="filter-icon">🔍</span>
              <span className="button-label">篩選條件</span>
            </button>
            
            {/* 小組選擇 - 根據跨組查看權限顯示或禁用 */}
            {allGroups.length > 0 && (
              <button 
                className="group-select-button"
                onClick={() => {
                  if (allowCrossGroupView) {
                    onGroupSelectDialogOpen(true);
                  }
                }}
                disabled={!allowCrossGroupView}
                title={allowCrossGroupView ? "選擇小組" : "老師已關閉跨組查看功能"}
                style={{
                  opacity: allowCrossGroupView ? 1 : 0.5,
                  cursor: allowCrossGroupView ? 'pointer' : 'not-allowed'
                }}
              >
                <span className="group-icon">👥</span>
                <span className="button-label">小組選擇</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* 小組選擇對話框 */}
      {currentWall === 'idea' && (
        <Dialog 
          open={isGroupSelectDialogOpen} 
          onClose={() => onGroupSelectDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              選擇查看小組
            </Typography>
          </DialogTitle>
          <DialogContent>
            {myGroupId && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 0.5 }}>
                  📍 您所在的小組
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {allGroups.find(g => String(g.id) === String(myGroupId))?.groupName || 
                   allGroups.find(g => String(g.id) === String(myGroupId))?.name || 
                   `小組 ${myGroupId}`}
                </Typography>
              </Box>
            )}
            {viewingGroupId && String(viewingGroupId) !== String(myGroupId) && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#856404', mb: 0.5 }}>
                  🔍 目前查看的小組
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {allGroups.find(g => String(g.id) === String(viewingGroupId))?.groupName || 
                   allGroups.find(g => String(g.id) === String(viewingGroupId))?.name || 
                   `小組 ${viewingGroupId}`}
                </Typography>
              </Box>
            )}
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {allGroups.map((group) => {
                const groupIdStr = String(group.id);
                const isMyGroup = myGroupId && String(myGroupId) === groupIdStr;
                const isViewing = viewingGroupId && String(viewingGroupId) === groupIdStr;
                const groupName = group.groupName || group.name || `小組 ${group.id}`;
                
                return (
                  <ListItem key={group.id} disablePadding>
                    <ListItemButton
                      onClick={() => onGroupChange(groupIdStr)}
                      selected={isViewing}
                      sx={{
                        backgroundColor: isViewing ? '#e3f2fd' : 'transparent',
                        borderLeft: isMyGroup ? '3px solid #1976d2' : 'none',
                        '&:hover': {
                          backgroundColor: isViewing ? '#e3f2fd' : '#f5f5f5'
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#e3f2fd',
                          '&:hover': {
                            backgroundColor: '#e3f2fd'
                          }
                        }
                      }}
                    >
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{groupName}</span>
                            {isMyGroup && (
                              <Typography 
                                component="span" 
                                variant="caption" 
                                sx={{ 
                                  bgcolor: '#1976d2', 
                                  color: 'white', 
                                  px: 0.75, 
                                  py: 0.25, 
                                  borderRadius: 1,
                                  fontSize: '0.7rem'
                                }}
                              >
                                我的小組
                              </Typography>
                            )}
                            {isViewing && (
                              <Typography 
                                component="span" 
                                variant="caption" 
                                sx={{ 
                                  bgcolor: '#4caf50', 
                                  color: 'white', 
                                  px: 0.75, 
                                  py: 0.25, 
                                  borderRadius: 1,
                                  fontSize: '0.7rem'
                                }}
                              >
                                查看中
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={isMyGroup ? '您所在的小組' : '點擊查看此小組的想法牆'}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
            {viewingGroupId && String(viewingGroupId) !== String(myGroupId) && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fff3cd', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: '#856404', fontSize: '0.875rem' }}>
                  ⚠️ 您正在查看其他小組的想法牆，無法進行任何操作（新增、編輯、刪除節點等）
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
            <Button onClick={() => onGroupSelectDialogOpen(false)}>
              關閉
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* 渲染選中的模態框 */}
      {renderSelectedModal(selectedModal)}

    </div>
  );
};

export default WallDrawer;
